// Keeps the checked variant pill visible inside its horizontal scroller.
// The picker is re-rendered on every variant change, which resets scrollLeft to 0,
// so a selected pill further along the list would otherwise scroll out of sight.
function scrollSelectedPillIntoView(container) {
  const selected = container.querySelector('input[type="radio"]:checked');
  const item = selected && selected.closest('.product-form__input--pill-item');
  if (!item) return;

  const containerRect = container.getBoundingClientRect();
  const itemRect = item.getBoundingClientRect();
  const isFullyVisible = itemRect.left >= containerRect.left - 1 && itemRect.right <= containerRect.right + 1;
  if (isFullyVisible) return;

  container.scrollLeft += itemRect.left - containerRect.left - (containerRect.width - itemRect.width) / 2;
}

// Check if a pill row has horizontal overflow and apply scrollbar styling.
function updateScrollbarVisibility(container) {
  if (!container) return;
  // A row that is not laid out yet — or the outgoing node mid view-transition —
  // reports zero width. Measuring it would wrongly strip the class.
  if (container.clientWidth === 0) return;

  if (container.scrollWidth > container.clientWidth) {
    container.classList.add('has-scrollbar');
  } else {
    container.classList.remove('has-scrollbar');
  }
}

// Rows that already have the drag listeners bound. The picker is replaced on every
// variant change, so new nodes bind fresh — this only stops a re-init of the same
// node from stacking a second set of listeners.
const pillRowsWithDragScroll = new WeakSet();

// Enable mouse drag-to-scroll on the pill row, with a click-vs-drag threshold.
// Suppresses native HTML drag behavior (🚫 cursor + ghost text).
// Dragging works anywhere on the row, including on pill labels.
function enablePillRowDragScroll(container) {
  if (!container || pillRowsWithDragScroll.has(container)) return;
  pillRowsWithDragScroll.add(container);

  const DRAG_THRESHOLD = 5; // pixels of movement before a click counts as a drag

  let activePointerId = null;
  let isDragging = false;
  let dragStartX = 0;
  let dragStartScrollLeft = 0;
  let suppressNextClick = false;

  const endGesture = () => {
    if (activePointerId !== null && container.hasPointerCapture(activePointerId)) {
      container.releasePointerCapture(activePointerId);
    }
    if (isDragging) {
      container.classList.remove('is-scrolling');
      container.style.userSelect = '';
      container.style.scrollBehavior = '';
    }
    activePointerId = null;
    isDragging = false;
  };

  container.addEventListener('pointerdown', (e) => {
    // Only handle a primary-button mouse press; touch pans natively.
    if (e.pointerType !== 'mouse' || e.button !== 0) return;

    activePointerId = e.pointerId;
    isDragging = false;
    suppressNextClick = false;
    dragStartX = e.clientX;
    dragStartScrollLeft = container.scrollLeft;

    // Deliberately NO setPointerCapture here. Capturing on pointerdown retargets the
    // follow-up click to this container, so the pill <label> never activates its radio
    // and the variant can no longer be selected. Capture is taken in pointermove, once
    // the gesture is known to be a drag.
  });

  container.addEventListener('pointermove', (e) => {
    if (e.pointerId !== activePointerId) return;

    if (!isDragging) {
      if (Math.abs(e.clientX - dragStartX) < DRAG_THRESHOLD) return;

      isDragging = true;
      container.classList.add('is-scrolling');
      container.style.userSelect = 'none';
      container.style.scrollBehavior = 'auto';
      container.setPointerCapture(e.pointerId);
    }

    container.scrollLeft = dragStartScrollLeft + (dragStartX - e.clientX);
    e.preventDefault();
  });

  container.addEventListener('pointerup', (e) => {
    if (e.pointerId !== activePointerId) return;

    suppressNextClick = isDragging;
    endGesture();
  });

  container.addEventListener('pointercancel', (e) => {
    if (e.pointerId !== activePointerId) return;

    suppressNextClick = false;
    endGesture();
  });

  // A drag that ends on top of a pill still fires a click. Swallow that one click so
  // scrolling the row never changes the variant as a side effect.
  container.addEventListener(
    'click',
    (e) => {
      if (!suppressNextClick) return;

      suppressNextClick = false;
      e.preventDefault();
      e.stopPropagation();
    },
    true
  );

  // Suppress native drag on the container.
  container.addEventListener('dragstart', (e) => {
    e.preventDefault();
  });

  container.setAttribute('draggable', 'false');
}

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initPillRows();
        this.initPillRowResizeHandler();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      // Measure every variant pill row, bring the checked pill into view, and bind
      // drag-to-scroll. Safe to call again after the picker is re-rendered.
      initPillRows() {
        const setup = () => {
          this.querySelectorAll('.product-form__input--pill-list').forEach((container) => {
            updateScrollbarVisibility(container);
            scrollSelectedPillIntoView(container);
            enablePillRowDragScroll(container);
          });
        };

        setup();
        // Re-measure after the next frame: scrollWidth is only trustworthy once the
        // row has been laid out.
        requestAnimationFrame(setup);
      }

      initPillRowResizeHandler() {
        let resizeTimer;

        this.onPillRowResize = () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            this.querySelectorAll('.product-form__input--pill-list').forEach((container) =>
              updateScrollbarVisibility(container)
            );
          }, 250);
        };

        window.addEventListener('resize', this.onPillRowResize, { passive: true });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();

        if (this.onPillRowResize) {
          window.removeEventListener('resize', this.onPillRowResize);
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Gtin', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('ProductStickyAddToCart');
          updateSourceFromDestination('ProductRrp');
          updateSourceFromDestination('PreorderShipDate');

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('preorder') ?? true,
            window.variantStrings.soldOut
          );

          // Sync per-variant pre-order button label + colours from the freshly rendered HTML
          const fetchedSubmit = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const liveSubmit = this.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
          if (fetchedSubmit && liveSubmit) {
            // Keep the pre-order class + attribute in sync with the variant so styling (e.g. the
            // hover brighten via .pd-button--preorder) matches — the fetched button is the source of truth.
            liveSubmit.classList.toggle('pd-button--preorder', fetchedSubmit.classList.contains('pd-button--preorder'));
            if (fetchedSubmit.hasAttribute('preorder')) {
              liveSubmit.setAttribute('preorder', '');
            } else {
              liveSubmit.removeAttribute('preorder');
            }

            const fetchedStyle = fetchedSubmit.getAttribute('style');
            if (fetchedStyle) {
              liveSubmit.setAttribute('style', fetchedStyle);
            } else {
              liveSubmit.removeAttribute('style');
            }
            const fetchedSpan = fetchedSubmit.querySelector('span');
            const liveSpan = liveSubmit.querySelector('span');
            if (fetchedSpan && liveSpan) {
              liveSpan.textContent = fetchedSpan.textContent.trim();
              const fetchedSpanStyle = fetchedSpan.getAttribute('style');
              if (fetchedSpanStyle) {
                liveSpan.setAttribute('style', fetchedSpanStyle);
              } else {
                liveSpan.removeAttribute('style');
              }
            }
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          this.initPillRows();
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Gtin', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        // Inside the quick-add modal every section id is prefixed (see QuickAddModal.preventDuplicatedIDs),
        // but the freshly fetched HTML still carries the original id. Rename the fetched gallery to match
        // before diffing, otherwise every existing slide is removed and the new active slide is never found.
        const originalSectionId = this.dataset.originalSection;
        if (mediaGalleryDestination && originalSectionId && originalSectionId !== this.dataset.section) {
          mediaGalleryDestination.innerHTML = mediaGalleryDestination.innerHTML.replaceAll(
            originalSectionId,
            this.dataset.section
          );
        }

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
